import {
  Avatar,
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useState } from 'react';
import { CollapsiblePanel } from './CollapsiblePanel';

export interface SidePanelItem {
  key: string;
  label: string;
  sublabel?: string;
  secondary?: string;
  statusColor?: string;
}

interface SidePanelProps {
  title: string;
  items: SidePanelItem[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  onAdd?: () => void;
  addLabel?: string;
  searchPlaceholder: string;
}

const getInitials = (label: string, sublabel?: string): string => {
  if (sublabel) return sublabel.charAt(0).toUpperCase();
  return label.charAt(0).toUpperCase();
};

export const SidePanel = ({
  title,
  items,
  selectedKey,
  onSelect,
  onAdd,
  addLabel,
  searchPlaceholder,
}: SidePanelProps) => {
  const theme = useTheme();
  const [search, setSearch] = useState('');

  const filtered = items.filter(
    (item) =>
      item.label.toLowerCase().includes(search.toLowerCase()) ||
      item.sublabel?.toLowerCase().includes(search.toLowerCase()),
  );

  const expandedContent = (
    <>
      {/* Search */}
      <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: alpha(theme.palette.text.primary, 0.35) }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: alpha(theme.table.stripeBg, 0.7),
              height: 32,
              fontSize: '0.75rem',
              transition: 'all 0.2s ease',
              '& fieldset': { borderColor: 'transparent' },
              '&:hover': {
                backgroundColor: theme.table.stripeBg,
                '& fieldset': { borderColor: alpha(theme.border.color, 0.5) },
              },
              '&.Mui-focused': {
                backgroundColor: '#fff',
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                '& fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1,
                },
              },
            },
          }}
        />
      </Box>

      {/* Item list */}
      <List sx={{ flex: 1, overflow: 'auto', py: 0.5, px: 0.5 }}>
        {filtered.map((item) => {
          const isSelected = selectedKey === item.key;
          const displayLabel = item.sublabel
            ? `${item.sublabel} ${item.label}`
            : item.label;

          return (
            <ListItemButton
              key={item.key}
              selected={isSelected}
              onClick={() => onSelect(item.key)}
              sx={{
                py: 0.625,
                px: 1.25,
                borderRadius: '8px',
                mb: 0.25,
                borderLeft: '3px solid transparent',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  borderLeftColor: alpha(theme.palette.primary.main, 0.3),
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  borderLeftColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: '0.8rem',
                    lineHeight: 1.4,
                    letterSpacing: '-0.005em',
                    color: isSelected
                      ? theme.palette.text.primary
                      : alpha(theme.palette.text.primary, 0.8),
                  }}
                >
                  {displayLabel}
                </Typography>
                {item.secondary && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {item.statusColor && (
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: item.statusColor, flexShrink: 0 }} />
                    )}
                    <Typography variant="caption" noWrap
                      sx={{ fontSize: '0.6rem', lineHeight: 1, color: alpha(theme.palette.text.primary, 0.45) }}>
                      {item.secondary}
                    </Typography>
                  </Box>
                )}
              </Box>
            </ListItemButton>
          );
        })}
      </List>
    </>
  );

  const collapsedContent = (
    <List sx={{ py: 0.5, px: 0.5 }}>
      {filtered.map((item) => {
        const isSelected = selectedKey === item.key;
        const displayLabel = item.sublabel
          ? `${item.sublabel} ${item.label}`
          : item.label;

        return (
          <Tooltip key={item.key} title={displayLabel} placement="right" arrow>
            <ListItemButton
              selected={isSelected}
              onClick={() => onSelect(item.key)}
              sx={{
                justifyContent: 'center',
                py: 0.5,
                px: 0.25,
                borderRadius: '8px',
                mb: 0.25,
                minHeight: 36,
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  },
                },
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: isSelected
                    ? theme.palette.primary.main
                    : alpha(theme.palette.primary.main, 0.08),
                  color: isSelected ? '#fff' : alpha(theme.palette.text.primary, 0.55),
                  transition: 'all 0.2s ease',
                }}
              >
                {getInitials(item.label, item.sublabel)}
              </Avatar>
            </ListItemButton>
          </Tooltip>
        );
      })}
    </List>
  );

  const expandedFooter = onAdd ? (
    <Box sx={{ p: 1 }}>
      <ListItemButton
        onClick={onAdd}
        sx={{
          py: 0.625,
          px: 1.25,
          borderRadius: '8px',
          justifyContent: 'center',
          color: theme.palette.primary.main,
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
          },
        }}
      >
        <AddIcon sx={{ fontSize: 16, mr: 0.5 }} />
        <Typography
          variant="body2"
          sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'inherit', letterSpacing: '-0.005em' }}
        >
          {(addLabel || '').replace(/^\+\s*/, '')}
        </Typography>
      </ListItemButton>
    </Box>
  ) : null;

  const collapsedFooter = onAdd ? (
    <Box sx={{ p: 0.5 }}>
      <Tooltip title={addLabel || ''} placement="right" arrow>
        <IconButton
          size="small"
          onClick={onAdd}
          sx={{
            width: '100%',
            height: 34,
            borderRadius: '8px',
            color: theme.palette.primary.main,
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            },
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  ) : null;

  return (
    <CollapsiblePanel
      title={title}
      count={items.length}
      footer={expandedFooter}
      collapsedContent={collapsedContent}
      collapsedFooter={collapsedFooter}
    >
      {expandedContent}
    </CollapsiblePanel>
  );
};
